import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactTags } from 'react-tag-autocomplete';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import { AppSettings } from './../../config/app-settings.js';

const recipientSuggestions = [
	{ value: 1, label: 'admin@seantheme.com' },
	{ value: 2, label: 'support@seantheme.com' },
	{ value: 3, label: 'sales@seantheme.com' }
];

function EmailRecipients({ label, recipients, onDelete, onAdd, actions }) {
	return (
		<div className="row mb-2 mailbox-to">
			<label className="col-form-label w-100px ps-2 pe-2 fw-500 text-end">{label}:</label>
			<div className="col position-relative">
				<ReactTags
					selected={recipients}
					suggestions={recipientSuggestions}
					allowNew
					onDelete={onDelete}
					onAdd={onAdd}
				/>
				{actions}
			</div>
		</div>
	);
}

function EmailCompose() {
	const context = useContext(AppSettings);
	const [to, setTo] = useState([{ value: 1, label: 'admin@seantheme.com' }]);
	const [cc, setCc] = useState([]);
	const [bcc, setBcc] = useState([]);
	const [visibleFields, setVisibleFields] = useState({ cc: false, bcc: false });
	const [text, setText] = useState('');

	useEffect(() => {
		context.handleSetAppContentClass('p-0');
		context.handleSetAppContentFullHeight(true);
		return () => {
			context.handleSetAppContentClass('');
			context.handleSetAppContentFullHeight(false);
		};
	}, [context]);

	const handleDeleteTo = useCallback((tagIndex) => {
		setTo((currentRecipients) => currentRecipients.filter((_, index) => index !== tagIndex));
	}, []);

	const handleAddTo = useCallback((newRecipient) => {
		setTo((currentRecipients) => [...currentRecipients, newRecipient]);
	}, []);

	const handleDeleteCc = useCallback((tagIndex) => {
		setCc((currentRecipients) => currentRecipients.filter((_, index) => index !== tagIndex));
	}, []);

	const handleAddCc = useCallback((newRecipient) => {
		setCc((currentRecipients) => [...currentRecipients, newRecipient]);
	}, []);

	const handleDeleteBcc = useCallback((tagIndex) => {
		setBcc((currentRecipients) => currentRecipients.filter((_, index) => index !== tagIndex));
	}, []);

	const handleAddBcc = useCallback((newRecipient) => {
		setBcc((currentRecipients) => [...currentRecipients, newRecipient]);
	}, []);

	const handleToggleField = useCallback((field) => {
		setVisibleFields((currentFields) => ({ ...currentFields, [field]: true }));
	}, []);

	return (
		<>
			<div className="mailbox">
				<div className="mailbox-toolbar">
					<div className="mailbox-toolbar-item">
						<span className="mailbox-toolbar-text">{'New Message'}</span>
					</div>
					<div className="mailbox-toolbar-item">
						<a href="#/" className="mailbox-toolbar-link active">
							{'Send'}
						</a>
					</div>
					<div className="mailbox-toolbar-item">
						<a href="#/" className="mailbox-toolbar-link">
							{'Attachment'}
						</a>
					</div>
					<div className="mailbox-toolbar-item">
						<Link to="/email/inbox" className="mailbox-toolbar-link">
							{'Discard'}
						</Link>
					</div>
					<div className="mailbox-toolbar-item dropdown">
						<a href="#/" className="mailbox-toolbar-link" data-bs-toggle="dropdown">
							<i className="fa fa-ellipsis-h"></i>
						</a>
						<div className="dropdown-menu ms-n1 position-fixed">
							<a href="#/" className="dropdown-item">
								{'Save draft'}
							</a>
							<a href="#/" className="dropdown-item">
								{'Show From'}
							</a>
							<a href="#/" className="dropdown-item">
								{'Check names'}
							</a>
							<a href="#/" className="dropdown-item">
								{'Set importance'}
							</a>
							<a href="#/" className="dropdown-item">
								{'Switch to plain text'}
							</a>
							<a href="#/" className="dropdown-item">
								{'Check for accessibility issues'}
							</a>
						</div>
					</div>
					<div className="mailbox-toolbar-item ms-auto">
						<a href="#/" className="mailbox-toolbar-link">
							<i className="fa fa-redo fa-fw fs-12px me-1"></i>
							{'Undo'}
						</a>
					</div>
					<div className="mailbox-toolbar-item">
						<Link to="/email/inbox" className="mailbox-toolbar-link">
							<i className="fa fa-times fa-fw"></i>
							{'Cancel'}
						</Link>
					</div>
				</div>
				<div className="mailbox-body">
					<div className="mailbox-content">
						<form action="#" method="POST" name="email_form" className="h-100">
							<div data-scrollbar="true" data-height="100%">
								<div className="mailbox-form">
									<div className="mailbox-form-header">
										<EmailRecipients
											label="To"
											recipients={to}
											onDelete={handleDeleteTo}
											onAdd={handleAddTo}
											actions={
												<div className="position-absolute end-0 top-0 d-flex align-items-center gap-3 px-3 pt-7px">
													{!visibleFields.cc && (
														<a
															href="#/"
															className="text-decoration-none fw-bold link-secondary"
															onClick={(event) => {
																event.preventDefault();
																handleToggleField('cc');
															}}
														>
															{'Cc'}
														</a>
													)}
													{!visibleFields.bcc && (
														<a
															href="#/"
															className="text-decoration-none fw-bold link-secondary"
															onClick={(event) => {
																event.preventDefault();
																handleToggleField('bcc');
															}}
														>
															{'Bcc'}
														</a>
													)}
							</div>
											}
										/>
										{visibleFields.cc && (
											<EmailRecipients
												label="Cc"
												recipients={cc}
												onDelete={handleDeleteCc}
												onAdd={handleAddCc}
											/>
										)}
										{visibleFields.bcc && (
											<EmailRecipients
												label="Bcc"
												recipients={bcc}
												onDelete={handleDeleteBcc}
												onAdd={handleAddBcc}
											/>
										)}
										<div className="row mb-2">
											<label className="col-form-label w-100px ps-2 pe-2 fw-500 text-end">
												{'Subject:'}
											</label>
											<div className="col">
												<input type="text" className="form-control" placeholder="Email subject" />
											</div>
										</div>
									</div>
									<div className="mailbox-input">
										<ReactQuill
											value={text}
											onChange={setText}
											className="h-100"
											aria-label="Email contents"
										/>
									</div>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		</>
	);
}

export default EmailCompose;
